interface ClientErrorDetails {
  source?: string;
  line?: number;
  column?: number;
  componentStack?: string;
}

const reportedErrors = new Set<string>();
let reportCount = 0;

export function initClientErrorReporter() {
  window.addEventListener('error', event => {
    reportClientError(event.error || event.message, {
      source: withoutQuery(event.filename),
      line: event.lineno,
      column: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', event => {
    reportClientError(event.reason);
  });
}

export function reportClientError(
  error: unknown,
  details: ClientErrorDetails = {},
) {
  if (reportCount >= 5) return;

  const normalized = normalizeError(error);
  const fingerprint = `${normalized.message}\n${normalized.stack || ''}`;
  if (reportedErrors.has(fingerprint)) return;

  reportedErrors.add(fingerprint);
  reportCount++;

  const csrfToken = document.cookie
    .split('; ')
    .find(cookie => cookie.startsWith('XSRF-TOKEN='))
    ?.split('=')
    .slice(1)
    .join('=');

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };
  if (csrfToken) {
    headers['X-XSRF-TOKEN'] = decodeURIComponent(csrfToken);
  }

  void fetch(new URL('api/v1/client-errors', document.baseURI), {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers,
    body: JSON.stringify({
      message: normalized.message.slice(0, 1000),
      stack: normalized.stack?.slice(0, 8000),
      page: `${window.location.origin}${window.location.pathname}`.slice(
        0,
        2048,
      ),
      source: details.source?.slice(0, 2048),
      line: details.line,
      column: details.column,
      componentStack: details.componentStack?.slice(0, 8000),
    }),
  }).catch(() => {
    // Reporting errors must never trigger another application error.
  });
}

function normalizeError(error: unknown): {message: string; stack?: string} {
  if (error instanceof Error) {
    return {message: error.message || error.name, stack: error.stack};
  }
  if (typeof error === 'string') {
    return {message: error};
  }

  try {
    return {message: JSON.stringify(error)};
  } catch {
    return {message: String(error)};
  }
}

function withoutQuery(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return undefined;
  }
}
